// src/pages/BalanceSheetPage.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useCompany } from '../components/CompanySelector';
import { AccountBalanceSummary, ReportResult } from '../dto/reports.dto';
import { formatCurrency, formatDate, groupBalanceSheetAccounts } from '../utils/helpers';
import ReportTable from '../components/ReportTable';
import LoadingSpinner from '../components/LoadingSpinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const BalanceSheetPage: React.FC = () => {
  const { selectedCompany } = useCompany();
  const [endDate, setEndDate] = useState<Date | null>(new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()))); // Today
  const [reportData, setReportData] = useState<AccountBalanceSummary[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBalanceSheet = async () => {
    if (!selectedCompany || !endDate) {
      setError('Please select a company and an end date.');
      setReportData(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get<ReportResult<AccountBalanceSummary[]>>(
        `${BACKEND_URL}/reports/balance-sheet`,
        {
          params: {
            companyId: selectedCompany.id,
            endDate: endDate.toISOString().split('T')[0],
          },
        }
      );
      setReportData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch Balance Sheet.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // if (selectedCompany && endDate) {
    //   fetchBalanceSheet();
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, endDate]); // Re-fetch when company or end date changes

  const handleExport = async () => {
    if (!reportData) {
      alert('No data to export!');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/reports/export-excel`,
        {
          reportType: 'balance-sheet',
          companyId: selectedCompany?.id,
          endDate: endDate?.toISOString().split('T')[0],
          reportName: 'Balance Sheet',
          // Send the already fetched data to prevent re-fetching on export
          data: reportData,
          includeInitialBalance: 'false',
        },
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Balance_Sheet_${selectedCompany?.name}_${formatDate(endDate!)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export report.');
    }
  };

  // const bsColumns = [
  //   { header: 'Account Code', accessor: 'code' },
  //   { header: 'Account Name', accessor: 'name' },
  //   { header: 'Account Type', accessor: 'accountType' },
  //   { header: 'Internal Group', accessor: 'internalGroup' },
  //   { header: 'Debit Total', accessor: (row: AccountBalanceSummary) => formatCurrency(row.debitTotal), className: 'text-right' },
  //   { header: 'Credit Total', accessor: (row: AccountBalanceSummary) => formatCurrency(row.creditTotal), className: 'text-right' },
  //   { header: 'Balance', accessor: (row: AccountBalanceSummary) => formatCurrency(row.balance), className: 'font-semibold text-right' },
  // ];
  
  const bsColumns = [
  { header: 'Account', accessor: 'name' },
  { header: 'Code', accessor: 'code' },
  {
    header: 'Balance',
    accessor: (row: AccountBalanceSummary) => formatCurrency(row.balance),
    className: 'text-right font-semibold',
  },
];

  const groupedData = reportData ? groupBalanceSheetAccounts(reportData) : null;

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">Balance Sheet Report</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-end">
        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">As of Date (End Date):</label>
          <DatePicker
            selected={endDate}
            onChange={(date: Date | null) => setEndDate(date)}
            dateFormat="yyyy-MM-dd"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
          />
        </div>
        <div className="col-span-1 md:col-span-2">
          <button
            onClick={fetchBalanceSheet}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            disabled={loading || !selectedCompany}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : reportData && reportData.length > 0 ? (
        <>
          <div className="mb-4">
            <button
              onClick={handleExport}
              className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Export to XLSX
            </button>
          </div>

          {groupedData && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Assets */}
              <div>
                <h3 className="text-xl font-bold text-gray-700 mb-3 border-b pb-2 border-gray-300">Assets</h3>
                <div className="mt-4 p-3 bg-blue-50 rounded-md shadow-inner text-right font-bold text-lg">
                  Total Assets: {formatCurrency(groupedData.totalAssets)}
                </div>
                <ReportTable columns={bsColumns} data={groupedData.assets} />
              </div>

              {/* Liabilities & Equity */}
              <div>
                <h3 className="text-xl font-bold text-gray-700 mb-3 border-b pb-2 border-gray-300">Liabilities & Equity</h3>
                <div className="mt-4 p-3 bg-blue-50 rounded-md shadow-inner text-right font-bold text-lg">
                  Total Liabilities & Equity: {formatCurrency(groupedData.totalLiabilitiesAndEquity)}
                </div>
                <ReportTable columns={bsColumns} data={groupedData.liabilities.concat(groupedData.equity)} />
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-gray-500 text-center">No data available for the selected criteria. Please generate the report.</p>
      )}
    </div>
  );
};

export default BalanceSheetPage;